import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import GeneralForm from '../../components/generalForm';
import { useItemCategoryEdit, useItemEdit, useItemFirstLevelCategories, useItemsForOrder } from './apiHooks';
import utils from '../../utils/utils';
import MultiSelect from '../../components/customFields/multiSelect';
import forms from '../../utils/forms';
import CustomSelect from '../../components/customFields/customSelect';

function AdminItemOrderingForm({ history }) {
  const intl = useIntl();

  const [itemCategory, setItemCategory] = useState({
    firstLevelCategory: '',
    secondLevelCategory: '',
    top6: [],
    top12: [],
    noOrder: [],
  });

  const { categories } = useItemFirstLevelCategories();

  const selectedFirstLevelCategory =
    itemCategory.firstLevelCategory && categories
      ? categories.find((it) => it.name === itemCategory.firstLevelCategory)
      : null;

  const selectedSecondLevelCategory =
    itemCategory.secondLevelCategory && selectedFirstLevelCategory
      ? selectedFirstLevelCategory.secondLevelCategories.find((it) => it.name === itemCategory.secondLevelCategory)
      : null;

  const {
    result,
    isLoading: loading,
    isError,
    error,
  } = useItemsForOrder({
    secondLevelCategory: itemCategory.secondLevelCategory,
    thirdLevelCategory: itemCategory.thirdLevelCategory,
    requiresThirdLevel: selectedSecondLevelCategory?.thirdLevelCategories.length,
    onSuccess: (result) => {
      setItemCategory({
        ...itemCategory,
        top6: result.items.filter((i) => i.ranking === 6).map((i) => i.id),
        top12: result.items.filter((i) => i.ranking === 12).map((i) => i.id),
      });
    },
  });

  const {
    editItemCategory,
    isLoading: editLoading,
    isError: isEditError,
    error: itemEditError,
  } = useItemCategoryEdit(() => window.alert('Saved!'));

  const itemOptions = result
    ? result.items.map((i) => ({
        value: i.id,
        label: utils.getItemName(i, intl.formatMessage),
      }))
    : [];

  const firstLevelCategoryOptions = forms.getFirstLevelCategoryOptions(categories, intl, true);
  const secondLevelCategoryOptions = forms.getSecondLevelCategoryOptions(selectedFirstLevelCategory, intl, true);
  const thirdLevelCategoryOptions = forms.getThirdLevelCategoryOptions(selectedSecondLevelCategory, intl, true);

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      firstLevelCategory: {
        type: 'string',
        title: intl.formatMessage({ id: 'item.form.field.first.level.category.label' }),
        options: firstLevelCategoryOptions,
        showClearOption: true,
      },
      secondLevelCategory: {
        type: 'string',
        title: intl.formatMessage({ id: 'item.form.field.second.level.category.label' }),
        options: secondLevelCategoryOptions,
        showClearOption: true,
      },
      thirdLevelCategory: thirdLevelCategoryOptions
        ? {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.third.level.category.label' }),
            options: thirdLevelCategoryOptions,
            showClearOption: true,
          }
        : null,
      top6: {
        type: 'array',
        title: intl.formatMessage({ id: 'admin.item.ordering.form.field.top.label' }, { top: 6 }),
        options: itemOptions,
      },
      top12: {
        type: 'array',
        title: intl.formatMessage({ id: 'admin.item.ordering.form.field.top.label' }, { top: 12 }),
        options: itemOptions,
      },
    },
  };

  if (!formSchema.properties.thirdLevelCategory) {
    delete formSchema.properties.thirdLevelCategory;
  }

  const uiSchema = {
    firstLevelCategory: {
      'ui:field': 'customSelect',
    },
    secondLevelCategory: {
      'ui:field': 'customSelect',
    },
    thirdLevelCategory: {
      'ui:field': 'customSelect',
    },
    top6: {
      'ui:field': 'multiSelect',
    },
    top12: {
      'ui:field': 'multiSelect',
    },
  };

  const onFormSubmit = () => {
    try {
      editItemCategory({ itemCategory });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  const formFields = {
    customSelect: CustomSelect,
    multiSelect: MultiSelect,
  };

  const buttonCopy = 'admin.item.ordering.form.submit';

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 admin__form">
        <GeneralForm
          title={intl.formatMessage({ id: 'admin.item.ordering.form.title' })}
          onChange={(event) => setItemCategory(event.formData)}
          onSubmit={onFormSubmit}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={itemCategory}
          buttonCopy={buttonCopy}
          errorMessage={isError || isEditError ? utils.getErrorMessageFromResponse(error || itemEditError, true) : null}
          loading={loading || editLoading}
          hideImageUpload={true}
        />
      </div>
    </div>
  );
}

export default AdminItemOrderingForm;
